import { WfnDropdownList, WfnRadioGroup } from '@adp-wfn/mdf-wc-react';
import React, { useCallback, useMemo } from 'react';
import { BaseField } from './BaseField';
import { useFieldLogic } from './hooks';
import { Field } from './types';

/**
 * Single-select field component (radio buttons or dropdown)
 * @param props - Field component props
 * @param props.data - Field configuration with options and value
 * @param props.onChange - Callback function for selection changes
 * @param props.isHorizontal - Whether to render radio buttons horizontally
 * @param props.renderPreference - Rendering preference (compact/expanded/auto)
 * @returns JSX element for single-select field
 */
export function OneOf({ data, onChange, isHorizontal, renderPreference = 'auto' }: Field) {
  const { useCompactControl } = useFieldLogic(data.options, renderPreference, 4);

  const options = useMemo(() => {
    if (!data.options || !Array.isArray(data.options)) {
      return [];
    }

    return data.options.map((item) => {
      return {
        ...item,
        label: item.label ?? item.text
      };
    });
  }, [data.options]);

  const handleChange = useCallback(
    (event: any) => onChange(event?.detail?.value),
    [onChange]
  );

  // temporary workaround to pass onSdfChange prop until WFNSUPP-240400 is resolved
  const props = {
    onSdfChange: handleChange
  };

  if (useCompactControl) {
    return (
      <BaseField>
        <WfnDropdownList
          {...props}
          data={options}
          label={data.label}
          required={data.required}
          tooltipText={data.tooltipText}
          value={data.value}
        />
      </BaseField>
    );
  }

  return (
    <BaseField>
      <WfnRadioGroup
        direction={isHorizontal ? 'row' : 'column'}
        items={options}
        label={data.label}
        required={data.required}
        tooltipText={data.tooltipText}
        value={data.value}
        onSdfChange={handleChange}
      />
    </BaseField>
  );
}
